import React, { useState, useEffect, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "../../assets/restaurant/logo.png";
import {
	Menu as MenuIcon,
	X,
	ChevronRight,
	ArrowRight,
	MessageCircle,
} from "lucide-react";

const primaryColor = "#43bd98";
const CTA_URL =
	"https://creator.applaby.com?onboarding=true&appType=Ecommerce&appSubtype=Restaurant";
const WHATSAPP_URL = "https://wa.me/34608916357";

const getInitialSection = () => {
	const { pathname, hash } = window.location;
	if (pathname === "/restaurantes/demo") return "demo";
	if (hash) return hash.replace("#", "");
	if (pathname === "/restaurantes" || pathname === "/restaurantes/")
		return "inicio";
	return "inicio";
};

const Menu = () => {
	const { t } = useTranslation();
	const [menuOpen, setMenuOpen] = useState(false);
	const [activeSection, setActiveSection] = useState(getInitialSection);

	const menuItems = [
		{ label: t("menu.home"), path: "/restaurantes" },
		{ label: t("menu.features"), id: "funcionalidades" },
		{ label: t("menu.carta"), path: "/restaurantes/carta" },
		{ label: t("menu.examples"), id: "ejemplos" },
		{ label: t("menu.pricing"), id: "precios" },
		{ label: t("menu.contact"), id: "contacto" },
	];

	const isDemo = window.location.pathname === "/restaurantes/demo";
	const isCarta = window.location.pathname === "/restaurantes/carta";

	useLayoutEffect(() => {
		const hash = window.location.hash.replace("#", "");
		if (!hash) return;
		const el = document.getElementById(hash);
		if (el) el.scrollIntoView({ behavior: "smooth" });
	}, []);

	useEffect(() => {
		const handleHashChange = () => {
			const hash = window.location.hash.replace("#", "");
			if (hash) {
				setActiveSection(hash);
				const el = document.getElementById(hash);
				if (el) el.scrollIntoView({ behavior: "smooth" });
			} else {
				setActiveSection("inicio");
			}
		};
		window.addEventListener("hashchange", handleHashChange);
		return () => window.removeEventListener("hashchange", handleHashChange);
	}, []);

	useEffect(() => {
		document.body.style.overflow = menuOpen ? "hidden" : "";
		return () => {
			document.body.style.overflow = "";
		};
	}, [menuOpen]);

	const isItemActive = (item) => {
		if (item.label === t("menu.home")) {
			return (
				activeSection === "inicio" &&
				(window.location.pathname === "/restaurantes" ||
					window.location.pathname === "/restaurantes/")
			);
		}
		if (item.path === "/restaurantes/carta") return isCarta;
		return Boolean(item.id && activeSection === item.id);
	};

	const goTo = (item) => {
		setMenuOpen(false);
		if (item.path) {
			window.location.href = item.path;
			return;
		}
		if (item.id) {
			if (isDemo || isCarta) {
				window.location.href = `/restaurantes/#${item.id}`;
			} else {
				window.location.hash = item.id;
			}
		}
	};

	const desktopLinks = menuItems.map((item) => {
		const isActive = isItemActive(item);
		return (
			<a
				key={item.label}
				href={item.path || `/restaurantes/#${item.id}`}
				onClick={(e) => {
					e.preventDefault();
					goTo(item);
				}}
				style={{
					textDecoration: "none",
					color: isActive ? primaryColor : "#444",
					fontWeight: isActive ? 600 : 500,
					fontSize: "0.875rem",
					padding: "0.3rem 0",
					transition: "color 0.3s ease",
					cursor: "pointer",
				}}
			>
				{item.label}
			</a>
		);
	});

	return (
		<header
			style={{
				position: "fixed",
				top: 0,
				left: 0,
				width: "100%",
				padding: "0.9rem 1.5rem",
				backgroundColor: menuOpen ? "#fff" : "#ffffff",
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				boxShadow: menuOpen ? "none" : "0 2px 8px rgba(0,0,0,0.05)",
				zIndex: 100,
				fontFamily: "'Poppins', sans-serif",
				boxSizing: "border-box",
			}}
		>
			<a
				href="/restaurantes"
				style={{
					display: "flex",
					alignItems: "center",
					position: "relative",
					zIndex: 2,
				}}
			>
				<img src={Logo} alt="Logo Applaby" style={{ height: "38px" }} />
			</a>

			<button
				type="button"
				onClick={() => setMenuOpen((prev) => !prev)}
				aria-label={menuOpen ? "Cerrar menú" : "Abrir menú"}
				aria-expanded={menuOpen}
				style={{
					background: menuOpen ? "#f3f4f6" : "none",
					border: menuOpen ? "1px solid #e5e7eb" : "none",
					borderRadius: "999px",
					cursor: "pointer",
					display: "none",
					alignItems: "center",
					justifyContent: "center",
					width: "40px",
					height: "40px",
					margin: "-8px",
					color: "#1a1a1a",
					position: "relative",
					zIndex: 2,
				}}
				className="menu-toggle"
			>
				{menuOpen ? <X size={20} strokeWidth={1.75} /> : <MenuIcon size={24} />}
			</button>

			<nav
				style={{
					display: "flex",
					alignItems: "center",
					gap: "1.8rem",
				}}
				className="nav-desktop"
			>
				{desktopLinks}
				<a
					href={CTA_URL}
					style={{
						padding: "0.7rem 1.5rem",
						background: `linear-gradient(135deg, ${primaryColor} 0%, #2fae83 100%)`,
						color: "#fff",
						borderRadius: "8px",
						fontSize: "0.875rem",
						fontWeight: 600,
						textDecoration: "none",
						whiteSpace: "nowrap",
						boxShadow: "0 4px 12px rgba(67,189,152,0.25)",
					}}
					target="_blank"
					rel="noreferrer"
				>
					{t("menu.start")}
				</a>
			</nav>

			<AnimatePresence>
				{menuOpen && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.25 }}
						style={{
							position: "fixed",
							inset: 0,
							zIndex: 1,
							display: "flex",
							flexDirection: "column",
							background: "#fff",
						}}
					>
						<div
							style={{
								pointerEvents: "none",
								position: "absolute",
								top: "-80px",
								right: "-40px",
								width: "280px",
								height: "280px",
								borderRadius: "50%",
								background: "rgba(67,189,152,0.12)",
								filter: "blur(80px)",
							}}
						/>
						<div
							style={{
								pointerEvents: "none",
								position: "absolute",
								bottom: "80px",
								left: "-60px",
								width: "240px",
								height: "240px",
								borderRadius: "50%",
								background: "rgba(67,189,152,0.08)",
								filter: "blur(70px)",
							}}
						/>

						<div style={{ height: "70px", flexShrink: 0 }} />

						<div
							style={{
								position: "relative",
								zIndex: 2,
								display: "flex",
								flexDirection: "column",
								flex: 1,
								minHeight: 0,
								padding: "8px 24px calc(24px + env(safe-area-inset-bottom))",
							}}
						>
							<p
								style={{
									margin: "0 0 28px",
									fontSize: "11px",
									fontWeight: 600,
									letterSpacing: "0.22em",
									textTransform: "uppercase",
									color: primaryColor,
								}}
							>
								{t("restaurantHeader.menuEyebrow")}
							</p>

							<nav
								style={{
									display: "flex",
									flexDirection: "column",
									flex: 1,
									overflowY: "auto",
									minHeight: 0,
								}}
							>
								{menuItems.map((item, i) => {
									const isActive = isItemActive(item);
									return (
										<motion.a
											key={item.label}
											href={item.path || `/restaurantes/#${item.id}`}
											onClick={(e) => {
												e.preventDefault();
												goTo(item);
											}}
											initial={{ opacity: 0, x: -16 }}
											animate={{ opacity: 1, x: 0 }}
											exit={{ opacity: 0, x: -8 }}
											transition={{ duration: 0.35, delay: 0.05 + i * 0.05 }}
											style={{
												display: "flex",
												alignItems: "center",
												gap: "16px",
												padding: "16px 0",
												borderBottom: "1px solid #f3f4f6",
												textDecoration: "none",
											}}
										>
											<span
												style={{
													width: "28px",
													fontSize: "11px",
													fontWeight: 500,
													fontVariantNumeric: "tabular-nums",
													color: isActive ? primaryColor : "rgba(67,189,152,0.7)",
												}}
											>
												{String(i + 1).padStart(2, "0")}
											</span>
											<span
												style={{
													flex: 1,
													fontSize: "1.5rem",
													fontWeight: 600,
													letterSpacing: "-0.03em",
													lineHeight: 1,
													color: isActive ? primaryColor : "#111827",
												}}
											>
												{item.label}
											</span>
											<ChevronRight
												size={18}
												color={isActive ? primaryColor : "#d1d5db"}
											/>
										</motion.a>
									);
								})}
							</nav>

							<motion.div
								initial={{ opacity: 0, y: 16 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.4, delay: 0.28 }}
								style={{ marginTop: "28px", display: "grid", gap: "12px" }}
							>
								<a
									href={CTA_URL}
									target="_blank"
									rel="noreferrer"
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: "8px",
										height: "52px",
										borderRadius: "16px",
										background: `linear-gradient(135deg, ${primaryColor} 0%, #2fae83 100%)`,
										color: "#fff",
										fontSize: "15px",
										fontWeight: 600,
										textDecoration: "none",
										boxShadow: "0 12px 40px rgba(67,189,152,0.28)",
									}}
								>
									{t("restaurantHeader.ctaPrimary")} <ArrowRight size={18} />
								</a>
								<a
									href={WHATSAPP_URL}
									target="_blank"
									rel="noreferrer"
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: "8px",
										height: "48px",
										borderRadius: "16px",
										border: "1px solid #e5e7eb",
										background: "#fafafa",
										color: "#374151",
										fontSize: "14px",
										fontWeight: 500,
										textDecoration: "none",
									}}
								>
									<MessageCircle size={18} color={primaryColor} />
									{t("restaurantHeader.menuWhatsapp")}
								</a>
								<p
									style={{
										margin: "4px 0 0",
										textAlign: "center",
										fontSize: "11px",
										color: "#9ca3af",
									}}
								>
									{t("restaurantHeader.menuTrust")}
								</p>
							</motion.div>
						</div>
					</motion.div>
				)}
			</AnimatePresence>

			<style>{`
				@media (max-width: 992px) {
					.nav-desktop { display: none !important; }
					.menu-toggle { display: flex !important; }
				}
			`}</style>
		</header>
	);
};

export default Menu;
